import { CircleMinus, Clock, FileText, ImageIcon, LayoutTemplate, type LucideIcon, Mic, Search, Sticker, Video } from 'lucide-react';
import { FC, useReducer, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import type { WhatsappConversation } from '@/services/endpoints/whatsappInbox';
import { contactLabel, listTime, matchesSearch, windowLeft } from '../inboxUtils';
import { ContactAvatar } from './ContactAvatar';

type Props = {
  conversations: WhatsappConversation[];
  selectedId?: string;
  onSelect: (id: string) => void;
};

const LAST_TYPE_ICON: Record<string, LucideIcon> = {
  image: ImageIcon,
  document: FileText,
  audio: Mic,
  video: Video,
  sticker: Sticker,
  template: LayoutTemplate,
};

const WindowStatus: FC<{ windowOpenUntil?: string | null }> = ({ windowOpenUntil }) => {
  const { t } = useTranslation('whatsappInbox');
  const left = windowLeft(windowOpenUntil);

  if (!left) {
    return (
      <span className='flex shrink-0 items-center gap-1 text-[11px] font-semibold text-primary-900/40'>
        <CircleMinus aria-hidden className='size-3' />
        {t('listWindowExpired')}
      </span>
    );
  }

  return (
    <span className='flex shrink-0 items-center gap-1 text-[11px] font-semibold text-emerald-600'>
      <Clock aria-hidden className='size-3' />
      {t(left.unit === 'hours' ? 'listWindowHoursLeft' : 'listWindowMinutesLeft', { count: left.count })}
    </span>
  );
};

export const ConversationList: FC<Props> = ({ conversations, selectedId, onSelect }) => {
  const { t } = useTranslation('whatsappInbox');
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [, tick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  const shown = conversations.filter((conversation) => matchesSearch(conversation, search) && (!unreadOnly || conversation.unreadCount > 0));
  const lastTypeLabel: Record<string, string> = {
    image: t('lastType.image'),
    document: t('lastType.document'),
    audio: t('lastType.audio'),
    video: t('lastType.video'),
    sticker: t('lastType.sticker'),
    template: t('lastType.template'),
  };
  const nothingShown = conversations.length === 0 ? t('empty') : unreadOnly && !search.trim() ? t('noUnread') : t('noResults');

  return (
    <aside className='flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden border-b border-primary-border md:min-w-[18rem] md:max-w-[24rem] md:flex-none md:border-b-0 md:border-r'>
      <div className='flex shrink-0 flex-col gap-2 border-b border-primary-background px-3.5 py-3.5'>
        <div className='relative flex min-w-0 items-center gap-2 rounded-lg border border-primary-border px-2.5'>
          <Search aria-hidden className='size-[15px] shrink-0 text-primary-900/40' />
          <input
            type='search'
            value={search}
            aria-label={t('searchPlaceholder')}
            placeholder={t('searchPlaceholder')}
            className='min-w-0 flex-1 border-0 bg-transparent py-2 text-[13px] outline-none'
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className='mt-0.5 flex flex-wrap gap-1.5'>
          {[false, true].map((only) => {
            const active = unreadOnly === only;
            return (
              <button
                key={String(only)}
                type='button'
                aria-pressed={active}
                className={twMerge(
                  'rounded-full border px-[11px] py-[5px] text-[11px] font-bold uppercase tracking-wide',
                  active ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-primary-border bg-white-100 text-primary-900/70 hover:border-brand-600'
                )}
                onClick={() => setUnreadOnly(only)}
              >
                {only ? t('filterUnread') : t('filterAll')}
              </button>
            );
          })}
        </div>
      </div>
      <ul className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
        {shown.length === 0 ? <li className='p-8 text-center text-[13px] text-primary-900/60'>{nothingShown}</li> : null}
        {shown.map((conversation) => {
          const unread = conversation.unreadCount > 0;
          const selected = conversation.id === selectedId;
          const TypeIcon = conversation.lastType ? LAST_TYPE_ICON[conversation.lastType] : undefined;
          return (
            <li key={conversation.id}>
              <button
                type='button'
                aria-current={selected}
                className={twMerge(
                  'flex w-full gap-3 border-b border-primary-background px-3.5 py-3 text-left hover:bg-primary-background',
                  selected && 'bg-brand-50 hover:bg-brand-50'
                )}
                onClick={() => onSelect(conversation.id)}
              >
                <ContactAvatar name={conversation.displayName} phone={conversation.contactPhone} />
                <span className='min-w-0 flex-1'>
                  <span className='flex items-center gap-2'>
                    <span className={twMerge('min-w-0 flex-1 truncate text-sm text-primary-900', unread ? 'font-bold' : 'font-semibold')}>
                      {contactLabel(conversation)}
                    </span>
                    <span className='shrink-0 text-[11px] text-primary-900/50'>{listTime(conversation.lastMessageAt, t('yesterday'))}</span>
                    {unread ? (
                      <span
                        role='img'
                        aria-label={t('unreadCount', { count: conversation.unreadCount })}
                        className='size-[9px] shrink-0 rounded-full bg-brand-600'
                      />
                    ) : null}
                  </span>
                  <span className={twMerge('mt-0.5 flex min-w-0 items-center gap-1 text-[13px]', unread ? 'text-primary-900' : 'text-primary-900/60')}>
                    {TypeIcon ? <TypeIcon aria-hidden className='size-3.5 shrink-0' /> : null}
                    <span className='truncate'>{conversation.lastPreview || (conversation.lastType ? lastTypeLabel[conversation.lastType] : '')}</span>
                  </span>
                  <span className='mt-1.5 flex min-w-0 items-center gap-2'>
                    {conversation.campaignName ? (
                      <span className='min-w-0 truncate rounded-md bg-primary-background px-[7px] py-0.5 text-[11px] text-primary-900/70'>
                        {conversation.campaignName}
                      </span>
                    ) : (
                      <span className='min-w-0 flex-1' />
                    )}
                    <span className='ml-auto'>
                      <WindowStatus windowOpenUntil={conversation.windowOpenUntil} />
                    </span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};
